import {isRedactedField,REDACTION_MARK} from '@/uptk/core/classification/redaction';
import {recordKey,relationValue} from './templateRelation';
import type {TemplateNode} from './templateTypes';

/**
 * CMDB-LAYOUT (GAP-DISPOSITION) : élément natif générique de disposition.
 * - `rack` : façade d'un conteneur à positions U (capacité, montages début/taille, catégorie de l'élément monté) ;
 * - `plan` : plan d'un conteneur (étage) avec ses zones (salles « contenu dans ») et ses objets positionnés (x, y normalisés), par couches.
 * Les positions sont des enregistrements du modèle et leurs relations ; le rendu reprend les pièces visuelles du prototype (`layout-slot`).
 * Aucune bibliothèque, aucun script : grille HTML ou SVG produit par le moteur, repli tabulaire lisible.
 */
export type LayoutValue={field:string;via?:string;entity?:string};
export type TemplateLayoutSource={
 container:{entity:string;display:string;parent?:LayoutValue;group?:LayoutValue;filter?:{field:string;value:string}};
 capacity?:{entity:string;container:string;field:string};
 positions:{entity:string;container:string;item:string;start?:string;size?:string;x?:string;y?:string;zone?:string};
 items:{entity:string;display:string;category?:LayoutValue};
 zones?:{entity:string;container:string;display:string;shape?:string};
 layers?:LayoutValue;
 overlays?:{label:string}[];
 /** Façade : emplacement du conteneur sur un plan (position de l'élément conteneur → étage et son parent, zone). */
 location?:{entity:string;item:string;floor:{field:string;entity:string;display:string;parent?:LayoutValue};zone?:{field:string;entity:string;display:string}};
};
export const LAYOUT_SLOTS=['units','capacity','occupied','occupancy','mounted','container','start','size','check','title','location','layers','plan','zones','items','zoom','empty'] as const;
export type LayoutSlot=typeof LAYOUT_SLOTS[number];
export type TemplateLayoutNode={type:'layout';kind:'rack'|'plan';source:TemplateLayoutSource;
 /** Valeur de catégorie (option du modèle) → classes du prototype pour la ligne U ou l'objet du plan. */variants?:Record<string,string>;
 /** Valeur de catégorie → classes de la pastille du prototype (liste des équipements montés). */badges?:Record<string,string>;
 labels?:{free?:string;continuation?:string;title?:string;location?:string;zones?:string;all?:string;region?:string};
 root:TemplateNode};
export type TemplateLayoutSlotNode={type:'layout-slot';slot:LayoutSlot;anchor?:TemplateNode;alternates?:TemplateNode[];byValue?:Record<string,number>};

type Row=Record<string,unknown>;
export const LAYOUT_LIMIT=500;
/** Entités lues par une disposition (une liste gouvernée par entité, au plus dix). */
export function layoutEntities(source:TemplateLayoutSource):string[]{
 const values=[source.container.entity,source.positions.entity,source.items.entity,source.capacity?.entity,source.zones?.entity,source.container.parent?.entity,source.container.group?.entity,source.items.category?.entity,source.layers?.entity,
  source.location?.entity,source.location?.floor.entity,source.location?.floor.parent?.entity,source.location?.zone?.entity];
 return [...new Set(values.filter((entity):entity is string=>!!entity))];
}
export const LAYOUT_MAX_ENTITIES=10;
/** Champs lus par une disposition (revue de protection, entités déclarées de la page) ; même liste que le validateur serveur. */
export function layoutReads(source:TemplateLayoutSource):{entity:string;field:string}[]{
 const out:{entity:string;field:string}[]=[];const add=(entity:string|undefined,field:string|undefined)=>{if(entity&&field)out.push({entity,field});};
 const value=(owner:string,item?:LayoutValue)=>{if(!item)return;if(item.via){add(owner,item.via);add(item.entity,item.field);}else add(owner,item.field);};
 const s=source;
 add(s.container.entity,s.container.display);add(s.container.entity,s.container.filter?.field);value(s.container.entity,s.container.parent);value(s.container.entity,s.container.group);
 for(const key of ['container','item','start','size','x','y','zone'] as const)add(s.positions.entity,s.positions[key]);
 add(s.items.entity,s.items.display);value(s.items.entity,s.items.category);
 if(s.capacity)add(s.capacity.entity,s.capacity.field);
 if(s.zones){add(s.zones.entity,s.zones.display);add(s.zones.entity,s.zones.shape);}
 value(s.items.entity,s.layers);
 if(s.location){add(s.location.entity,s.location.item);add(s.location.entity,s.location.floor.field);add(s.location.floor.entity,s.location.floor.display);value(s.location.floor.entity,s.location.floor.parent);if(s.location.zone){add(s.location.entity,s.location.zone.field);add(s.location.zone.entity,s.location.zone.display);}}
 return out;
}
const finite=(value:unknown):number|null=>{const number=typeof value==='string'&&value.trim()!==''?Number(value):value;return typeof number==='number'&&Number.isFinite(number)?number:null;};
const scalar=(value:unknown)=>typeof value==='string'||typeof value==='number';
export const sameKey=(a:unknown,b:unknown)=>scalar(a)&&scalar(b)&&String(a)===String(b);
/** Valeur affichable d'un champ ; une valeur caviardée reste caviardée, une valeur absente vaut null. */
export function layoutText(row:Row|undefined,field:string):string|null{
 if(!row)return null;
 if(isRedactedField(row,field)||isRedactedField(row,recordKey(row,field)))return REDACTION_MARK;
 const value=row[recordKey(row,field)];
 return typeof value==='string'&&value.trim()?value:typeof value==='number'&&Number.isFinite(value)?String(value):null;
}
export const layoutNumber=(row:Row|undefined,field:string|undefined)=>row&&field&&!isRedactedField(row,field)&&!isRedactedField(row,recordKey(row,field))?finite(row[recordKey(row,field)]):null;
/** Valeur reliée : champ propre, ou champ d'un enregistrement référencé (`via` → `entity`). */
export function linkedValue(row:Row|undefined,value:LayoutValue|undefined,lookup:(entity:string)=>Row[]):{raw:unknown;text:string|null}{
 if(!row||!value)return {raw:undefined,text:null};
 if(!value.via){const text=layoutText(row,value.field);return {raw:relationValue(row,value.field),text};}
 const key=relationValue(row,value.via);
 if(isRedactedField(row,value.via))return {raw:undefined,text:REDACTION_MARK};
 const target=lookup(value.entity??'').find(candidate=>sameKey(relationValue(candidate,'id'),key));
 return {raw:target?relationValue(target,value.field):undefined,text:layoutText(target,value.field)};
}

export type RackPosition={key:string;label:string;category?:string;start:number;size:number;end:number};
export type RackUnit={u:number;position?:RackPosition;top:boolean};
export type RackModel={capacity:number|null;units:RackUnit[];positions:RackPosition[];unplaced:string[];overlaps:string[];outside:string[];occupied:number;occupancy:number|null};
/** Façade : unités de la plus haute à la plus basse (comme une baie vue de face), occupation, chevauchements et dépassements explicites. */
export function rackModel(capacity:number|null,positions:{key:string;label:string;category?:string;start:number|null;size:number|null}[]):RackModel{
 const cap=capacity!==null&&Number.isInteger(capacity)&&capacity>0&&capacity<=100?capacity:null;
 const placed:RackPosition[]=[],unplaced:string[]=[],outside:string[]=[],overlaps:string[]=[];
 for(const item of positions.slice(0,LAYOUT_LIMIT)){
  const start=item.start,size=item.size??1;
  if(start===null||!Number.isInteger(start)||start<1||!Number.isInteger(size)||size<1){unplaced.push(item.label);continue;}
  const position={key:item.key,label:item.label,category:item.category,start,size,end:start+size-1};
  if(cap!==null&&position.end>cap)outside.push(item.label);
  placed.push(position);
 }
 const height=cap??Math.max(0,...placed.map(position=>position.end));
 const owner=new Map<number,RackPosition>();
 for(const position of [...placed].sort((a,b)=>a.start-b.start||a.key.localeCompare(b.key)))for(let u=position.start;u<=Math.min(position.end,height);u++){
  const previous=owner.get(u);
  if(previous&&previous!==position){if(!overlaps.includes(position.label))overlaps.push(position.label);continue;}
  owner.set(u,position);
 }
 const units:RackUnit[]=[];
 for(let u=height;u>=1;u--){const position=owner.get(u);units.push({u,position,top:!!position&&(u===Math.min(position.end,height)||owner.get(u+1)!==position)});}
 const occupied=new Set([...owner.keys()].filter(u=>u<=height)).size;
 return {capacity:cap,units,positions:placed,unplaced,overlaps,outside,occupied,occupancy:cap?occupied/cap:null};
}
/** Contrôle d'une plage avant placement : aucune écriture, résultat explicite. */
export function rackRange(model:RackModel,start:number|null,size:number|null):'invalid'|'outside'|'occupied'|'free'{
 if(start===null||size===null||!Number.isInteger(start)||!Number.isInteger(size)||start<1||size<1)return 'invalid';
 const end=start+size-1;
 if(model.capacity!==null&&end>model.capacity)return 'outside';
 return model.positions.some(position=>position.start<=end&&position.end>=start)?'occupied':'free';
}
export const unitLabel=(u:number,height:number)=>'U'+String(u).padStart(String(Math.max(height,1)).length,'0');

export type PlanPoint=[number,number];
/** Contour normalisé [0,1] : tableau JSON de points, ou « x,y x,y … » ; au plus 200 points, sinon aucun contour. */
export function parseShape(value:unknown):PlanPoint[]|null{
 let points:unknown=value;
 if(typeof value==='string'){const text=value.trim();if(text.length>20000)return null;
  if(text.startsWith('[')||text.startsWith('{')){try{points=JSON.parse(text);}catch{return null;}}
  else points=text.split(/\s+/).map(pair=>pair.split(',').map(Number));
 }
 if(points&&typeof points==='object'&&!Array.isArray(points)&&Array.isArray((points as {points?:unknown}).points))points=(points as {points:unknown}).points;
 if(!Array.isArray(points)||points.length<3||points.length>200)return null;
 const out:PlanPoint[]=[];
 for(const point of points){
  const pair=Array.isArray(point)?point:point&&typeof point==='object'?[(point as {x?:unknown}).x,(point as {y?:unknown}).y]:[];
  const x=finite(pair[0]),y=finite(pair[1]);
  if(x===null||y===null||x<0||x>1||y<0||y>1)return null;
  out.push([x,y]);
 }
 return out;
}
export type PlanZone={key:string;label:string;points:PlanPoint[]|null};
export type PlanItem={key:string;label:string;x:number;y:number;layer?:string;zone?:string};
export type PlanModel={zones:PlanZone[];items:PlanItem[];unplaced:string[]};
export function planModel(zones:PlanZone[],items:{key:string;label:string;x:number|null;y:number|null;layer?:string;zone?:string}[]):PlanModel{
 const placed:PlanItem[]=[],unplaced:string[]=[];
 for(const item of items.slice(0,LAYOUT_LIMIT)){
  if(item.x===null||item.y===null||item.x<0||item.x>1||item.y<0||item.y>1){unplaced.push(item.label);continue;}
  placed.push({key:item.key,label:item.label,x:item.x,y:item.y,layer:item.layer,zone:item.zone});
 }
 return {zones:zones.slice(0,LAYOUT_LIMIT),items:placed,unplaced};
}
export type ViewBox={x:number;y:number;width:number;height:number};
export function parseViewBox(value:string|undefined):ViewBox|null{
 const parts=(value??'').trim().split(/[\s,]+/).map(Number);
 return parts.length===4&&parts.every(Number.isFinite)&&parts[2]>0&&parts[3]>0?{x:parts[0],y:parts[1],width:parts[2],height:parts[3]}:null;
}
export const ZOOM={min:0.1,max:20,step:1.25};
export function zoomedViewBox(box:ViewBox,zoom:number):ViewBox{
 const factor=Math.min(ZOOM.max,Math.max(ZOOM.min,zoom));
 const width=box.width/factor,height=box.height/factor;
 return {x:box.x+(box.width-width)/2,y:box.y+(box.height-height)/2,width,height};
}
export const viewBoxText=(box:ViewBox)=>[box.x,box.y,box.width,box.height].map(value=>String(Math.round(value*1000)/1000)).join(' ');
const tenth=(value:number)=>String(Math.round(value*10)/10);
export const shapePath=(points:PlanPoint[],box:ViewBox)=>points.map(([x,y],index)=>(index?'L':'M')+tenth(box.x+x*box.width)+' '+tenth(box.y+y*box.height)).join(' ')+' Z';
export function shapeOrigin(points:PlanPoint[],box:ViewBox):[number,number]{
 return [box.x+Math.min(...points.map(point=>point[0]))*box.width,box.y+Math.min(...points.map(point=>point[1]))*box.height];
}
/** Slots d'une disposition : un slot hors de sa disposition n'est jamais rendu. */
export function layoutSlots(node:TemplateNode,out:TemplateLayoutSlotNode[]=[]):TemplateLayoutSlotNode[]{
 if(node.type==='layout-slot'){out.push(node);return out;}
 if(node.type==='el')node.children.forEach(child=>layoutSlots(child,out));
 return out;
}
